import Editor from "../../Components/common/Editor";
import trans from "./../../../Languages/Languages";
import { useState, useEffect } from "react";
import { Button, Card, message, InputNumber, Form } from "antd";
import API from "../../../API";
import { usePageContext } from "../../../Context/PageContext";

const Assessment = () => {
  const [editorHtml, setEditorHtml] = useState("");
  const handleTextEditorChange = (html) => setEditorHtml(html);

  const [assessmentData, setAssessmentData] = useState([
    { name: "", key: "session_test", slug: "session-test" },
    { name: "", key: "passing_marks", slug: "pass-marks" },
    { name: "", key: "mid_term", slug: "mid-term-exam" },
    { name: "", key: "assignments", slug: "assignments" },
    { name: "", key: "final_exam", slug: "final-exam" },
    { name: "", key: "attendence", slug: "attendance" },
  ]);

  const [editingIndex, setEditingIndex] = useState(null);
  const [renameValue, setRenameValue] = useState("");

  const [data, setData] = useState({});
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [toRefreshData, setToRefreshData] = useState(false);

  const [pageData, setPageData] = useState(null);
  const [isRenamingMain, setIsRenamingMain] = useState(false);
  const [renameMainTitle, setRenameMainTitle] = useState("");
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [contextMenuPos, setContextMenuPos] = useState({ x: 0, y: 0 });
  const [contextMenuTarget, setContextMenuTarget] = useState(null);
  const { triggerRefresh } = usePageContext();

  useEffect(() => {
    setLoading(true);
    const init = async () => {
      try {
        const res = await API.get("/admin/assessments");
        const { assessment, page } = res.data;
        setPageData(page);
        setRenameMainTitle(page?.title);
        setData(assessment);
        setEditorHtml(assessment.about);
        form.setFieldsValue(assessment);

        const labelMap = {
          "session-test": res.data.sessionTest,
          "pass-marks": res.data.passMarks,
          "mid-term-exam": res.data.midTermExam,
          "final-exam": res.data.finalExam,
          "attendance": res.data.attendance,
          "assignments": res.data.assignments,
        };

        setAssessmentData((prev) =>
          prev.map((item) => ({
            ...item,
            name: labelMap[item.slug]?.title,
          }))
        );
      } catch (err) {
        console.error("Fetch error", err);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [toRefreshData]);

  useEffect(() => {
    const handleClickOutside = () => setShowContextMenu(false);
    window.addEventListener("click", handleClickOutside);
    return () => window.removeEventListener("click", handleClickOutside);
  }, []);

  const handleSave = (values) => {
    setLoading(true);
    const formData = new FormData();
    formData.append("about", editorHtml);
    Object.keys(values).forEach((key) => {
      formData.append(key, values[key]);
    });

    API.post("/admin/assessments/update", formData)
      .then(() => {
        message.success("Updated Successfully");
        setToRefreshData(!toRefreshData);
      })
      .catch(() => {
        message.error("Something went wrong");
      })
      .finally(() => setLoading(false));
  };

  const handleMainRenameSubmit = async () => {
    try {
      await API.put(`/admin/pages/slug/${pageData.slug}`, { title: renameMainTitle });
      setPageData((prev) => ({ ...prev, title: renameMainTitle }));
      setIsRenamingMain(false);
      triggerRefresh();
    } catch {
      message.error("Rename failed");
    }
  };

  const handleItemRenameSubmit = async (item, index) => {
    try {
      await API.put(`/admin/pages/slug/${item.slug}`, { title: renameValue });
      const updated = [...assessmentData];
      updated[index].name = renameValue;
      setAssessmentData(updated);
      setEditingIndex(null);
    } catch {
      message.error("Rename failed");
    }
  };

  const handleContextRename = () => {
    if (contextMenuTarget === "main") {
      setIsRenamingMain(true);
    } else if (typeof contextMenuTarget === "number") {
      setEditingIndex(contextMenuTarget);
      setRenameValue(assessmentData[contextMenuTarget]?.name);
    }
    setShowContextMenu(false);
  };

  return (
    <div className="pages-container">
      <Card loading={loading} bordered={false} className="position-static">
        {!loading && (
          <div className="content-wrapper">
            <div className="bg-content-container">
              <div style={{ position: "relative" }}>
                <div
                  className="fw-600 mb-2"
                  onDoubleClick={() => setIsRenamingMain(true)}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    setContextMenuTarget("main");
                    setContextMenuPos({ x: e.clientX, y: e.clientY });
                    setShowContextMenu(true);
                  }}
                >
                  {isRenamingMain ? (
                    <input
                      type="text"
                      value={renameMainTitle}
                      autoFocus
                      onChange={(e) => setRenameMainTitle(e.target.value)}
                      onBlur={handleMainRenameSubmit}
                      onKeyDown={(e) => e.key === "Enter" && handleMainRenameSubmit()}
                      style={{ padding: "2px 6px", fontSize: "14px" }}
                    />
                  ) : (
                    pageData?.title || "Loading..."
                  )}
                </div>
              </div>

              {showContextMenu && (
                <ul
                  style={{
                    position: "fixed",
                    top: contextMenuPos.y,
                    left: contextMenuPos.x,
                    backgroundColor: "white",
                    border: "1px solid #ccc",
                    borderRadius: 4,
                    listStyle: "none",
                    padding: "5px 10px",
                    margin: 0,
                    zIndex: 1000,
                    cursor: "pointer",
                    boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                  }}
                  onClick={handleContextRename}
                  onMouseLeave={() => setShowContextMenu(false)}
                >
                  <li>{trans.Rename}</li>
                </ul>
              )}

              <Editor
                key="assessment-editor"
                editorHtml={editorHtml}
                setEditorHtml={setEditorHtml}
                handleChange={handleTextEditorChange}
              />
            </div>

            <Form form={form} onFinish={handleSave}>
              <div className="bg-content-container d-flex justify-content-center">
                <div className="assessment-details row justify-content-start mt-4 align-items-center">
                  {assessmentData.map((item, index) => (
                    <div
                      className="col-6 assessment-details d-flex  align-items-center"
                      key={index}
                      onContextMenu={(e) => {
                        e.preventDefault();
                        setContextMenuTarget(index);
                        setContextMenuPos({ x: e.clientX, y: e.clientY });
                        setShowContextMenu(true);
                      }}
                      onDoubleClick={() => {
                        setEditingIndex(index);
                        setRenameValue(item.name);
                        setShowContextMenu(false);
                      }}
                    >
                      <div className="me-3" style={{ minWidth: "150px" }}>
                        {editingIndex === index ? (
                          <input
                            type="text"
                            value={renameValue}
                            autoFocus
                            onChange={(e) => setRenameValue(e.target.value)}
                            onBlur={() => handleItemRenameSubmit(item, index)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") handleItemRenameSubmit(item, index);
                            }}
                            style={{ padding: "2px 6px", fontSize: "14px", width: "80%", }}
                          />
                        ) : (
                          <div className="f-12 fw-500 assessment-content-heading">{item.name}</div>
                        )}
                      </div>
                      <Form.Item name={item.key} initialValue={data[item.key]} className="mb-0">
                        <InputNumber
                          className="assessment-input"
                          max={100}
                          formatter={(value) => `${value}%`}
                          parser={(value) => value.replace("%", "")}
                        />
                      </Form.Item>
                    </div>
                  ))}
                </div>
              </div>
              <Button className="btn-save-changes" type="primary" htmlType="submit">
                {trans.Save}
              </Button>
            </Form>
          </div>
        )}
      </Card>
    </div>
  );
};

export default Assessment;